<script setup lang="ts">
/**
 * AI features page body. The page title band is rendered by
 * MarketingFeaturePage, so this opens with a plain section heading, then the
 * template's two service cards, a content block with the before/after demo
 * next to a dark "powered by" panel, and a single button.
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const features = [
  {
    id: 'textEnhancement',
    icon: 'bi-pencil-square',
    points: ['improve', 'professional', 'generate'],
  },
  {
    id: 'jobMatching',
    icon: 'bi-clipboard-data',
    points: ['compatibility', 'skills', 'ats'],
  },
] as const
</script>

<template>
  <section class="sofax relative overflow-hidden bg-white dark:bg-gray-950">
    <div class="section-space">
      <div class="shell relative z-10">
        <SofaxSectionHeading
          :eyebrow="t('homepage.aiFeatures.menu')"
          :title="t('homepage.aiFeatures.title')"
          :text="t('homepage.aiFeatures.subtitle')"
        />

        <div class="grid grid-cols-1 gap-y-20 lg:gap-y-[6.25rem]">
          <!-- Service cards: what the AI does -->
          <ul class="grid grid-cols-1 gap-6 md:grid-cols-2">
            <li v-for="feature in features" :key="feature.id" class="card card-hover h-full p-[1.875rem]">
              <SofaxIconTile :icon="feature.icon" />
              <div class="display-heading display-heading-4 mb-4 mt-[1.875rem]">
                {{ t(`homepage.aiFeatures.${feature.id}.title`) }}
              </div>
              <p class="mb-6 text-base">{{ t(`homepage.aiFeatures.${feature.id}.description`) }}</p>
              <ul class="flex flex-col gap-3">
                <li v-for="point in feature.points" :key="point" class="flex items-start gap-3">
                  <i class="bi bi-check-circle-fill mt-0.5 shrink-0 text-xl text-accent" />
                  <span class="text-base">{{ t(`homepage.aiFeatures.${feature.id}.features.${point}`) }}</span>
                </li>
              </ul>
            </li>
          </ul>

          <!-- Content block: the demo on the left, the dark panel on the right -->
          <div class="grid grid-cols-1 items-center gap-10 md:gap-[3.75rem] lg:grid-cols-2 xl:grid-cols-[1fr_minmax(0,0.8fr)] xl:gap-20">
            <div>
              <div class="mb-[3.125rem]">
                <h2 class="!text-3xl md:!text-[2.5rem]">{{ t('homepage.aiFeatures.demo.title') }}</h2>
                <p class="mt-5">{{ t('homepage.aiFeatures.demo.description') }}</p>
              </div>

              <div class="flex max-w-[35rem] flex-col gap-4">
                <div class="card">
                  <div class="mb-2 text-sm font-semibold uppercase tracking-wide text-muted dark:text-gray-400">
                    {{ t('homepage.aiFeatures.demo.before') }}
                  </div>
                  <p class="text-base">“{{ t('homepage.aiFeatures.demo.beforeText') }}”</p>
                </div>

                <div class="flex justify-center" aria-hidden="true">
                  <SofaxIconTile icon="bi-arrow-down" round size="sm" />
                </div>

                <div class="card border-accent bg-accent-soft/20 dark:bg-accent/10">
                  <div class="mb-2 text-sm font-semibold uppercase tracking-wide text-accent">
                    {{ t('homepage.aiFeatures.demo.after') }}
                  </div>
                  <p class="text-base font-medium text-ink dark:text-white">“{{ t('homepage.aiFeatures.demo.afterText') }}”</p>
                </div>
              </div>
            </div>

            <div class="relative mx-auto w-full max-w-[31.25rem]">
              <div class="rounded-[0.625rem] bg-ink p-[1.875rem] text-white dark:bg-gray-800 md:p-10">
                <SofaxIconTile icon="bi-stars" round size="md" />
                <div class="display-heading display-heading-3 mb-3 mt-8 !text-white">
                  {{ t('homepage.aiFeatures.demo.powered') }}
                </div>
                <p class="!text-white/75">{{ t('homepage.aiFeatures.demo.technology') }}</p>
              </div>
              <img
                src="/sofax/content-shape-1.svg"
                alt=""
                aria-hidden="true"
                width="125"
                height="137"
                class="pointer-events-none absolute -right-10 -top-10 -z-10 hidden xl:inline-block"
              >
            </div>
          </div>

          <div class="text-center">
            <SofaxBtn
              :to="isAuthenticated ? '/admin/premium' : '/registration'"
              :label="t('homepage.aiFeatures.cta')"
              variant="accent"
            />
          </div>
        </div>
      </div>

      <img
        src="/sofax/hero-1-element-2.svg"
        alt=""
        aria-hidden="true"
        width="200"
        height="200"
        class="pointer-events-none absolute -right-10 top-24 -z-10 hidden opacity-70 xl:inline-block"
      >
    </div>
  </section>
</template>
